import React, { useEffect, useState } from 'react';
import Map, { Source, Layer } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { CENTRAL_EUROPE_LOCATION } from '../../../constants/map.constants';

const MapComponent = ({
  className,
  center = CENTRAL_EUROPE_LOCATION,
  style,
  markers,
  onClick,
  zoom = 3,
  selectedMarker,
  setSelectedMarker,
  selectedPopupComponent,
  popupMaxWidthClassName,
}) => {
  const [curCoordsChanged, setCurCoordsChanged] = useState(false);
  const [styleLoaded, setStyleLoaded] = useState(false);
  const mapRef = React.useRef(null);

  useEffect(() => {
    setCurCoordsChanged(true);
  }, [center]);

  useEffect(() => {
    if (!curCoordsChanged) return;
    setCurCoordsChanged(false);
  }, [curCoordsChanged]);

  function handleClick(e) {
    if (onClick) {
      onClick(e);
    }

    const features = e.target.queryRenderedFeatures(e.point);
    if (features.length > 0) {
      const clickedFeature = features[0];
      if (clickedFeature?.properties?.cluster) {
        if (mapRef.current) {
          mapRef.current.flyTo({
            center: clickedFeature.geometry.coordinates,
            zoom: mapRef.current.getZoom() + 1,
          });
        }
      } else {
        const marker = markers?.find(marker => marker?.id === clickedFeature?.properties?.id);
        if (marker) {
          setSelectedMarker && setSelectedMarker(marker);
        }
      }
    }
  }

  return (
    <div className={`w-100 h-100 ${className}`}>
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: center.lng,
          latitude: center.lat,
          zoom: zoom,
        }}
        style={style}
        onClick={handleClick}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
        mapStyle={process.env.REACT_APP_MAPBOX_STYLE}
        onLoad={() => setStyleLoaded(true)}
        dragRotate={false}
        touchRotate={false}
        attributionControl={false}
      >
        {styleLoaded && markers && (
          <Source
            id="markers"
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: markers
                .filter(marker => marker !== null)
                .map(marker => ({
                  type: 'Feature',
                  properties: {
                    id: marker?.id,
                  },
                  geometry: {
                    type: 'Point',
                    coordinates: [marker?.coords?.lng, marker?.coords?.lat],
                  },
                })),
            }}
            cluster={true}
            clusterMaxZoom={14}
            clusterRadius={50}
          >
            <Layer
              id="clusters"
              type="circle"
              source="markers"
              filter={['has', 'point_count']}
              paint={{
                'circle-color': [
                  'step',
                  ['get', 'point_count'],
                  '#008fd4',
                  100,
                  '#008fd4',
                  750,
                  '#008fd4',
                ],
                'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
              }}
            />
            <Layer
              id="cluster-count"
              type="symbol"
              source="markers"
              filter={['has', 'point_count']}
              layout={{
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 14,
              }}
              paint={{
                'text-color': '#ffffff'
              }}
            />
            <Layer
              id="unclustered-point"
              type="circle"
              source="markers"
              filter={['!', ['has', 'point_count']]}
              paint={{
                'circle-color': '#008fd4',
                'circle-radius': 6,
                'circle-stroke-width': 1,
                'circle-stroke-color': '#fff',
              }}
            />
          </Source>
        )}
      </Map>
    </div>
  );
};

export default MapComponent;
